import { Injectable } from '@angular/core';
import { Auth } from '@aws-amplify/auth';
import { AuthService } from '../auth/auth.service';
import { CommonUtil } from '../util/common-util';

export interface EventOperationManager {
  id: string;
  name: string;
  position?: string | null;
  affiliations?: string[];
}

export interface EventOperationReleaseControl {
  mode: 'off' | 'paused' | 'enabled';
  canWrite: boolean;
  reason: string;
  message: string;
}

export interface EventOperationSettings {
  featuresVisible?: boolean;
  eventId: number;
  eventTitle?: string;
  attendanceSelected: boolean;
  surveySelected: boolean;
  attendanceKind: 'officer' | 'guest' | 'unset';
  sourceParticipantCategory?: number | null;
  revision: number;
  legacyAttendance: boolean;
  hasAttendanceData: boolean;
  hasSurveyData: boolean;
  operationalBrief: {
    venue?: string | null;
    targetOrganization?: string | null;
    participantCategory?: string | null;
    schedules: Array<{
      dayIndex: number;
      eventDate?: string | null;
      startTime?: string | null;
      endTime?: string | null;
    }>;
  };
  managers: EventOperationManager[];
  capabilities: {
    canConfigure: boolean;
    canOperate: boolean;
    planStatus: string;
    isAssignedManager?: boolean;
  };
  releaseControls: {
    attendance: EventOperationReleaseControl;
    survey: EventOperationReleaseControl;
  };
}

export interface AssignedOperationEvent {
  eventId: number;
  title: string;
  attendanceSelected: boolean;
  surveySelected: boolean;
  attendanceKind: 'officer' | 'guest' | 'unset';
  hasAttendanceData: boolean;
  hasSurveyData: boolean;
}

@Injectable({ providedIn: 'root' })
export class EventOperationApiService {
  private readonly path = '/myevent/operations';

  constructor(private auth: AuthService, private commonUtil: CommonUtil) {}

  private async request<T>(body: object): Promise<T> {
    const session = await Auth.currentSession();
    const options = { ...this.auth.createApiHeader(session), body };
    const response = await this.commonUtil.apiPost(this.path, options);
    return response.data as T;
  }

  get(eventId: number): Promise<EventOperationSettings> {
    return this.request<EventOperationSettings>({ action: 'get', id: eventId });
  }

  brochure(eventId:number):Promise<any>{return this.request({action:'brochure',id:eventId});}
  saveBrochure(eventId:number,body:object):Promise<any>{return this.request({action:'save_brochure',id:eventId,...body});}

  async assigned(): Promise<AssignedOperationEvent[]> {
    const result = await this.request<{ items: AssignedOperationEvent[] }>({ action: 'assigned' });
    return result.items ?? [];
  }

  save(
    eventId: number,
    settings: Pick<EventOperationSettings, 'attendanceSelected' | 'surveySelected' | 'revision'>,
    managerIds: string[],
    reason: string,
  ): Promise<EventOperationSettings> {
    return this.request<EventOperationSettings>({
      action: 'save',
      id: eventId,
      attendanceSelected: settings.attendanceSelected,
      surveySelected: settings.surveySelected,
      expectedRevision: settings.revision,
      managerIds,
      reason,
    });
  }
}
